import React, { useState } from "react"
import { useDispatch } from "react-redux"
import { Form, Formik } from "formik"
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import { labelTemplates as ALL_LABEL_TEMPLATES } from "../../../../utils/productLabels"
import { updateEnterpriseLabelTemplates } from "../../../../actions/Organizations/Enterprise"

const templateKey = (t) => `${t.value}__${t.brand}`
const keyToTemplate = (key) => {
  const [value, brand] = key.split("__")
  return { value, brand }
}

const sx = {
  dialog: {
    "& .MuiDialog-paper": {
      width: 640,
      maxWidth: "calc(100vw - 32px)",
      borderRadius: "8px",
    },
  },
  title: {
    px: 5,
    pt: 3,
    pb: 1,
    fontSize: 18,
    fontWeight: 500,
    pr: 8,
    marginBottom: "20px !important",
  },
  closeBtn: {
    position: "absolute",
    right: 16,
    top: 16,
    color: "text.secondary",
  },
  content: {
    px: 5,
    pt: 1,
    pb: 2,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: 400,
    color: "#757575",
    mb: 1,
  },
  placeholder: {
    fontSize: 14,
    color: "#9E9E9E",
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    py: "4px",
  },
  serverError: {
    px: 5,
    pb: 1,
    fontSize: 14,
    color: "#B71C1C",
  },
  actions: {
    px: 5,
    pt: 3,
    pb: 4,
    justifyContent: "space-between",
    borderTop: "1px solid",
    borderColor: "divider",
  },
}

const EditLabelTemplatesModal = ({ open, onClose, enterpriseId, selected }) => {
  const dispatch = useDispatch()
  const [serverError, setServerError] = useState(null)

  const initialValues = {
    labelTemplates: selected.map(templateKey),
  }

  const handleSubmit = async (values, { setSubmitting }) => {
    setServerError(null)
    try {
      await dispatch(updateEnterpriseLabelTemplates(enterpriseId, values.labelTemplates.map(keyToTemplate)))
      onClose()
    } catch (e) {
      setServerError("Une erreur est survenue, veuillez réessayer")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} sx={sx.dialog}>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue, isSubmitting }) => (
          <Form>
            <DialogTitle sx={sx.title}>
              Modifier le template des étiquettes
              <IconButton onClick={onClose} disabled={isSubmitting} sx={sx.closeBtn}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={sx.content}>
              <Box>
                <Typography sx={sx.fieldLabel}>Template des étiquettes</Typography>
                <Select
                  fullWidth
                  multiple
                  variant="standard"
                  displayEmpty
                  value={values.labelTemplates}
                  onChange={(e) => setFieldValue("labelTemplates", e.target.value)}
                  MenuProps={{
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                    PaperProps: { style: { maxHeight: 300 } },
                  }}
                  renderValue={(keys) => keys.length === 0 ? (
                    <Typography sx={sx.placeholder}>Sélectionner des templates</Typography>
                  ) : (
                    <Box sx={sx.chips}>
                      {ALL_LABEL_TEMPLATES.filter(t => keys.includes(templateKey(t))).map(t => (
                        <Chip key={templateKey(t)} label={t.label} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {ALL_LABEL_TEMPLATES.map((t) => (
                    <MenuItem key={templateKey(t)} value={templateKey(t)}>
                      <Checkbox checked={values.labelTemplates.includes(templateKey(t))} />
                      <ListItemText primary={t.label} />
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </DialogContent>

            {serverError && (
              <Typography sx={sx.serverError}>{serverError}</Typography>
            )}

            <DialogActions sx={sx.actions}>
              <Button variant="text" onClick={onClose} disabled={isSubmitting}>
                Annuler
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {isSubmitting ? "Enregistrement…" : serverError ? "Réessayer" : "Enregistrer"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

export default EditLabelTemplatesModal
